import { Colors, Lightning } from '@lightningjs/sdk';
import { ProgressBar as ProgressBarLightning } from '@lightningjs/ui';
import PlaybackProgressBar, {
  FASTFORWARD_ICON,
  PROGRESS_BAR_HEIGHT,
  PROGRESS_BAR_WIDTH,
  REWIND_ICON,
} from 'components/common/progressBar/PlaybackProgressBar';
import { translate } from 'support/translate';
import { getFontFaceFromStyle } from 'support/textUtils';
import { AdCuePoint } from 'components/pages/playback/ad-managers/AbstractAdManager';
import { formatVideoTime, getVideoTimeTts } from 'support/contentUtils';
import { HoverableComponent } from 'components/common/HoverableComponent';
import TextLabel from 'components/common/TextLabel';

const FOCUSED_ALPHA = 1;
const UNFOCUSED_ALPHA = 0.5;

const PROGRESS_BAR_CONTAINER_H = 89;
const SEEK_BALL_DIAMETER = 30;
const AD_POD_MARKER_WIDTH = 8;

const FONT_SIZE = 29;
const MARGIN_SMALL = 8;
const MARGIN_LARGE = 16;

const DEFAULT_SEEK_RATE = 1;
const DEFAULT_SEEK_SPEED = 0;

export interface VodProgressBarTemplateSpec
  extends Lightning.Component.TemplateSpec {
  progress: number;
  duration: number;
  seekRate: number;
  seekSpeed: number;
  adCuePoints: AdCuePoint[];

  VideoData: {
    VideoTime: typeof TextLabel;
    SeekInfo: {
      SeekIcon: Lightning.textures.ImageTexture;
      SeekSpeed: typeof TextLabel;
    };
  };
  ProgressBar: {
    LoadedBar: typeof ProgressBarLightning;
    PlaybackProgressBar: typeof PlaybackProgressBar;
    AdPodMarkers: object;
    SeekBall: Lightning.textures.RectangleTexture;
  };
}

export default class VodProgressBar
  extends HoverableComponent<VodProgressBarTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<VodProgressBarTemplateSpec>
{
  private _progress = 0;
  private _duration = 0;
  private _seekRate = DEFAULT_SEEK_RATE;
  private _seekSpeed = DEFAULT_SEEK_SPEED;
  private _adCuePoints: VodProgressBarTemplateSpec['adCuePoints'] = [];

  private _ProgressBar = this.getByRef('ProgressBar')!;
  private _LoadedBar = this._ProgressBar.getByRef('LoadedBar')!;
  private _PlaybackProgressBar = this._ProgressBar.getByRef(
    'PlaybackProgressBar',
  )!;
  private _AdPodMarkers = this._ProgressBar.getByRef('AdPodMarkers')!;
  private _SeekBall = this._ProgressBar.getByRef('SeekBall')!;
  private _VideoData = this.getByRef('VideoData')!;

  private _VideoTime = this._VideoData.getByRef('VideoTime')!;
  private _SeekInfo = this._VideoData.getByRef('SeekInfo')!;

  private _SeekIcon = this._SeekInfo.getByRef('SeekIcon')!;
  private _SeekSpeed = this._SeekInfo.getByRef('SeekSpeed')!;

  get announce() {
    const [progress, duration] = this._VideoTime.text?.text.split(' / ') ?? [];
    const progressTts = getVideoTimeTts(progress ?? '');
    const durationTts = getVideoTimeTts(duration ?? '');
    const phrase = translate(
      'ttsPrompts.progressBar',
      progressTts,
      durationTts,
    );

    return phrase;
  }

  get progress() {
    return this._progress;
  }

  set progress(progress: VodProgressBarTemplateSpec['progress']) {
    this._progress = progress;
    this.updateProgressBar(progress);
    this.updateVideoData(progress);
  }

  get duration() {
    return this._duration;
  }

  set duration(duration: VodProgressBarTemplateSpec['duration']) {
    this._duration = duration;
    this.updateVideoData(this.progress);
    this.renderAdPodMarkers();
  }

  get seekRate() {
    return this._seekRate;
  }

  set seekRate(seekRate: VodProgressBarTemplateSpec['seekRate']) {
    this._seekRate = seekRate;
  }

  get seekSpeed() {
    return this._seekSpeed;
  }

  set seekSpeed(seekSpeed: VodProgressBarTemplateSpec['seekSpeed']) {
    this._seekSpeed = seekSpeed;
    this.updateVideoData(this.progress);
  }

  get adCuePoints() {
    return this._adCuePoints;
  }

  set adCuePoints(adCuePoints: VodProgressBarTemplateSpec['adCuePoints']) {
    this._adCuePoints = adCuePoints;
    this.renderAdPodMarkers();
  }

  get playbackProgressBar() {
    return this._PlaybackProgressBar;
  }
  static override _template(): Lightning.Component.Template<Lightning.Component.TemplateSpecLoose> {
    return {
      w: PROGRESS_BAR_WIDTH,
      h: PROGRESS_BAR_CONTAINER_H,
      flex: {
        direction: 'column',
      },
      alpha: UNFOCUSED_ALPHA,
      VideoData: {
        flex: { direction: 'row' },
        flexItem: {
          marginBottom: MARGIN_SMALL,
        },
        VideoTime: {
          type: TextLabel,
          flexItem: {
            marginRight: MARGIN_LARGE,
          },
          text: {
            fontSize: FONT_SIZE,
            text: translate('playback.defaultTime'),
            fontFace: getFontFaceFromStyle('bold'),
            textColor: Colors('text').get(),
          },
        },
        SeekInfo: {
          flex: { direction: 'row' },
          visible: false,
          SeekIcon: {
            flexItem: {
              marginRight: MARGIN_SMALL,
            },
          },
          SeekSpeed: {
            type: TextLabel,
            text: {
              fontSize: FONT_SIZE,
              fontFace: getFontFaceFromStyle('bold'),
              textColor: Colors('text').get(),
            },
          },
        },
      },
      ProgressBar: {
        LoadedBar: {
          type: ProgressBarLightning,
          w: PROGRESS_BAR_WIDTH,
          h: PROGRESS_BAR_HEIGHT,
          progressColor: Colors('white').alpha(0.7).get(),
          backgroundColor: Colors('transparent').get(),
          progressRadius: PROGRESS_BAR_HEIGHT / 2,
          backgroundRadius: PROGRESS_BAR_HEIGHT / 2,
          value: 0,
        },
        PlaybackProgressBar: {
          type: PlaybackProgressBar,
          w: PROGRESS_BAR_WIDTH,
          h: PROGRESS_BAR_HEIGHT,
          progressRadius: PROGRESS_BAR_HEIGHT / 2,
          value: 0,
          passSignals: { $onHover: true, $onCursorClick: true },
        },
        AdPodMarkers: {},
        SeekBall: {
          y: PROGRESS_BAR_HEIGHT / 2,
          mount: 0.5,
          rect: true,
          color: Colors('white').get(),
          w: SEEK_BALL_DIAMETER,
          h: SEEK_BALL_DIAMETER,
          shader: {
            type: Lightning.shaders.RoundedRectangle,
            radius: SEEK_BALL_DIAMETER / 2,
          },
          visible: false,
        },
      },
    };
  }

  override _getFocused() {
    return this._PlaybackProgressBar;
  }

  override _focus() {
    this._SeekBall.visible = true;
    this.setSmooth('alpha', FOCUSED_ALPHA);
  }

  override _unfocus() {
    this._SeekBall.visible = false;
    this.setSmooth('alpha', UNFOCUSED_ALPHA);
  }

  private getSanitizedProgress(progress: number) {
    if (progress > this.duration) return this.duration;
    if (progress < 0) return 0;
    return progress;
  }

  private updateProgressBar(progress: number) {
    progress = this.getSanitizedProgress(progress);

    const percentage = progress / this.duration;
    this._PlaybackProgressBar.value = percentage;
    const position = percentage * this._PlaybackProgressBar.w;
    if (!isNaN(position)) {
      this._SeekBall.x = position;
    }
  }

  private renderAdPodMarkers() {
    this._AdPodMarkers.childList.clear();
    this.adCuePoints.forEach(adCuePoint => {
      if (!adCuePoint.played && adCuePoint.start !== 0) {
        this._AdPodMarkers.add({
          rect: true,
          x: (adCuePoint.start / this.duration) * PROGRESS_BAR_WIDTH,
          y: 1,
          w: AD_POD_MARKER_WIDTH,
          h: PROGRESS_BAR_HEIGHT - 2,
          color: Colors('black').alpha(0.78).get(),
        });
      }
    });
  }

  private updateVideoData(progress: number) {
    progress = this.getSanitizedProgress(progress);

    this._VideoData.patch({
      VideoTime: {
        text: {
          text: translate(
            'playback.progressTimestamp',
            formatVideoTime(progress),
            formatVideoTime(this.duration),
          ),
        },
      },
      SeekInfo: {
        visible: this.seekSpeed !== 0,
        SeekIcon: this.seekRate > 0 ? FASTFORWARD_ICON : REWIND_ICON,
        SeekSpeed: {
          text: {
            text: translate('playback.seekSpeed', this.seekSpeed),
          },
        },
      },
    });
  }

  hideAdPodMarkers() {
    this._AdPodMarkers.visible = false;
  }

  showAdPodMarkers() {
    this._AdPodMarkers.visible = true;
  }
}
